import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import api from "../api/axios";
import {
  getOrganTreeByGender,
  formatOrganName,
  getOrganSubparts,
} from "../constants/hraOrgans";
import "./D3OrganTreePanel.css";

const RISK_COLORS = {
  high: "#ef4444",
  moderate: "#f59e0b",
  low: "#22c55e",
  normal: "#64748b",
};

export default function D3OrganTreePanel({
  result,
  gender,
  patient,
  selectedChemicals,
  patientResearchContext,
  onOrganSelect,
  onImpactsFromMedgen,
}) {
  const svgRef = useRef(null);
  const expandedSvgRef = useRef(null);
  const zoomRef = useRef(null);
  const expandedZoomRef = useRef(null);

  const [selectedOrgan, setSelectedOrgan] = useState("");
  const [layoutMode, setLayoutMode] = useState("horizontal");
  const [streaming, setStreaming] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([
    {
      role: "assistant",
      content:
        "Ask Medgen about drug-combination impact, patient history, BMI, current conditions, or a highlighted organ.",
    },
  ]);

  const organTree = useMemo(() => getOrganTreeByGender(gender), [gender]);
  const impactedOrgans = result?.organs || {};
  const [graphExpanded, setGraphExpanded] = useState(false);
  const [pendingImpacts, setPendingImpacts] = useState([]);

  const treeData = useMemo(() => {
    return {
      name: gender === "female" ? "Female Body" : "Male Body",
      label: gender === "female" ? "Female Body" : "Male Body",
      type: "root",
      children: Object.entries(organTree).map(([system, organs]) => ({
        name: system,
        label: system,
        type: "system",
        children: organs.map((organ) => {
          const organKey = String(organ).toLowerCase();
          const subparts = getOrganSubparts(organKey);
          const impact = impactedOrgans[organKey] || null;

          return {
            name: organKey,
            label: formatOrganName(organKey),
            type: "organ",
            risk: impact?.risk || "normal",
            score: impact?.score || 0,
            data: impact,
            children: subparts.map((part) => ({
              name: `${organKey}.${part}`,
              organ: organKey,
              subpart: part,
              label: formatOrganName(part),
              type: "subpart",
              risk: impact?.risk || "normal",
              score: impact?.score || 0,
              data: impact,
            })),
          };
        }),
      })),
    };
  }, [organTree, gender, impactedOrgans]);

  useEffect(() => {
    renderTree(svgRef.current, zoomRef);

    if (graphExpanded) {
      setTimeout(() => {
        renderTree(expandedSvgRef.current, expandedZoomRef);
      }, 50);
    }
  }, [treeData, selectedOrgan, layoutMode, graphExpanded]);

  const renderTree = (svgEl, activeZoomRef) => {
    if (!svgEl) return;
    const width = 1250;
    const height = 820;

    const root = d3.hierarchy(treeData);

    const treeLayout =
      layoutMode === "horizontal"
        ? d3.tree().nodeSize([42, 260])
        : d3.tree().nodeSize([145, 105]);

    treeLayout(root);

    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();

    svg
      .attr("viewBox", [0, 0, width, height])
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("class", "d3-organ-tree-svg");

    const defs = svg.append("defs");

    defs
      .append("marker")
      .attr("id", "treeArrow")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 9)
      .attr("refY", 0)
      .attr("markerWidth", 7)
      .attr("markerHeight", 7)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "rgba(148, 163, 184, 0.75)");

    const glow = defs
      .append("filter")
      .attr("id", "nodeGlow")
      .attr("x", "-60%")
      .attr("y", "-60%")
      .attr("width", "220%")
      .attr("height", "220%");

    glow
      .append("feGaussianBlur")
      .attr("stdDeviation", "4")
      .attr("result", "coloredBlur");

    const merge = glow.append("feMerge");
    merge.append("feMergeNode").attr("in", "coloredBlur");
    merge.append("feMergeNode").attr("in", "SourceGraphic");

    const container = svg.append("g").attr("class", "tree-zoom-container");

    const initialTransform =
      layoutMode === "horizontal"
        ? d3.zoomIdentity.translate(95, height / 2)
        : d3.zoomIdentity.translate(width / 2, 80);

    const zoom = d3
      .zoom()
      .scaleExtent([0.25, 3.2])
      .on("zoom", (event) => {
        container.attr("transform", event.transform);
      });

    activeZoomRef.current = zoom;
    svg.call(zoom);
    svg.call(zoom.transform, initialTransform);

    container
      .append("g")
      .attr("fill", "none")
      .attr("stroke", "rgba(148, 163, 184, 0.52)")
      .attr("stroke-width", 1.5)
      .selectAll("path")
      .data(root.links())
      .join("path")
      .attr("class", "tree-link")
      .attr("marker-end", "url(#treeArrow)")
      .attr("d", (d) => makeElbowPath(d, layoutMode));

    const nodes = container
      .append("g")
      .selectAll("g")
      .data(root.descendants())
      .join("g")
      .attr("class", (d) => `tree-node tree-node-${d.data.type}`)
      .attr("transform", (d) => {
        const [x, y] = getNodePosition(d, layoutMode);
        return `translate(${x}, ${y})`;
      })
      .style("cursor", (d) =>
        d.data.type === "organ" || d.data.type === "subpart"
          ? "pointer"
          : "grab"
      )
      .on("click", (_, d) => {
        if (d.data.type !== "organ" && d.data.type !== "subpart") return;

        const organName = d.data.type === "subpart" ? d.data.organ : d.data.name;

        setSelectedOrgan(organName);
        onOrganSelect?.(organName);
      });

    nodes.each(function (d) {
      const group = d3.select(this);
      const label = d.data.label || d.data.name;
      const dims = getNodeDims(d, label);
      const isSelected =
        d.data.name === selectedOrgan ||
        (d.data.type === "subpart" && d.data.organ === selectedOrgan);

      const fill = getNodeFill(d);
      const stroke = isSelected ? "#ffffff" : getNodeStroke(d);

      group
        .append("rect")
        .attr("x", -dims.width / 2)
        .attr("y", -dims.height / 2)
        .attr("width", dims.width)
        .attr("height", dims.height)
        .attr("rx", 10)
        .attr("fill", fill)
        .attr("stroke", stroke)
        .attr("stroke-width", isSelected ? 3 : 1.3)
        .attr(
          "filter",
          d.data.risk === "high" || isSelected ? "url(#nodeGlow)" : null
        );

      group
        .append("text")
        .attr("class", "tree-node-label")
        .attr("text-anchor", "middle")
        .attr("dy", d.data.risk !== "normal" && d.data.type !== "system" ? "-0.15em" : "0.35em")
        .text(label);

      if (
        (d.data.type === "organ" || d.data.type === "subpart") &&
        d.data.risk !== "normal"
      ) {
        group
          .append("text")
          .attr("class", "tree-risk-label")
          .attr("text-anchor", "middle")
          .attr("dy", "1.3em")
          .text(`${d.data.risk} · ${d.data.score}`);
      }
    });

    const firstImpacted = root
      .descendants()
      .find(
        (d) =>
          (d.data.type === "organ" || d.data.type === "subpart") &&
          d.data.risk !== "normal"
      );

    if (firstImpacted) {
      const [x, y] = getNodePosition(firstImpacted, layoutMode);
      const focus =
        layoutMode === "horizontal"
          ? d3.zoomIdentity.translate(width * 0.46 - x, height * 0.5 - y).scale(0.95)
          : d3.zoomIdentity.translate(width * 0.5 - x, height * 0.42 - y).scale(0.95);

      svg.transition().duration(450).call(zoom.transform, focus);
    }
  };

  
  const zoomBy = (factor, expanded = false) => {
    const svgEl = expanded ? expandedSvgRef.current : svgRef.current;
    const activeZoom = expanded ? expandedZoomRef.current : zoomRef.current;

    if (!svgEl || !activeZoom) return;

    d3.select(svgEl).transition().duration(220).call(activeZoom.scaleBy, factor);
  };

const resetZoom = (expanded = false) => {
  const svgEl = expanded ? expandedSvgRef.current : svgRef.current;
  const activeZoom = expanded ? expandedZoomRef.current : zoomRef.current;

  if (!svgEl || !activeZoom) return;

  const width = 1250;
  const height = 820;

  const transform =
    layoutMode === "horizontal"
      ? d3.zoomIdentity.translate(95, height / 2)
      : d3.zoomIdentity.translate(width / 2, 80);

    d3.select(svgEl)
      .transition()
      .duration(300)
      .call(activeZoom.transform, transform);
  };

  const selectedOrganData = selectedOrgan ? impactedOrgans[selectedOrgan] : null;

  const askMedgenCombination = async (questionOverride) => {
    const question =
      questionOverride ||
      chatInput.trim() ||
      "Analyse this patient history and selected drug combination. Return impacted organs.";

    setChatInput("");
    setStreaming(true);

    setChatMessages((prev) => [
      ...prev,
      { role: "user", content: question },
      { role: "assistant", content: "Connecting to Medgen..." },
    ]);

    const updateLastAssistantMessage = (content) => {
      setChatMessages((prev) => {
        const copy = [...prev];

        if (!copy.length || copy[copy.length - 1]?.role !== "assistant") {
          copy.push({ role: "assistant", content });
          return copy;
        }

        copy[copy.length - 1] = {
          ...copy[copy.length - 1],
          content,
        };

        return copy;
      });
    };

    try {
      const token = localStorage.getItem("token");

      const selectedChemicalNames = (selectedChemicals || [])
        .map((c) => c.name)
        .filter(Boolean)
        .join(", ");

      const response = await fetch(
        `${api.defaults.baseURL}/api/research/ask-llm-impact/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            chemical_name:
              selectedChemicalNames || "Current selected drug combination",
            formula: null,
            molecular_weight: null,
            category: "drug_combination",
            patient_gender: gender || null,
            height_cm: patientResearchContext?.height_cm || null,
            weight_kg: patientResearchContext?.weight_kg || null,
            bmi: result?.bmi || null,
            notes: JSON.stringify(
              {
                question,
                selected_organ: selectedOrgan || null,
                selected_patient: patient || null,
                patient_research_context: patientResearchContext || null,
                selected_drug_combination: selectedChemicals || [],
                current_simulation_result: result || null,
                available_organ_tree: organTree,
                instruction:
                  "Return multiple organ impacts if relevant. Avoid duplicates. Consider patient history, current conditions, BMI, gender, drug combination, available organ tree, and organ subparts.",
              },
              null,
              2
            ),
          }),
        }
      );

      if (!response.ok || !response.body) {
        throw new Error("Medgen stream failed");
      }

      await readMedgenStream(response, {
        onStart: () => {
          updateLastAssistantMessage("Starting Medgen analysis...");
        },

        onProgress: (message) => {
          updateLastAssistantMessage(message || "Medgen is working...");
        },

        onFinal: (data) => {
        const rows = Array.isArray(data?.impacts) ? data.impacts : [];

        setPendingImpacts(rows);

        setChatMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = {
            role: "assistant",
            content: `Structured impact ready. ${rows.length} organ impacts found. Review and apply to simulation if needed.`,
          };
          return copy;
        });

        setStreaming(false);
      },

        onDone: () => {
          setStreaming(false);
        },
      });
    } catch (err) {
      updateLastAssistantMessage(err?.message || "Medgen failed.");
      setStreaming(false);
    }
  };

  return (
    <aside className="d3-organ-intel-panel">
      <div className="d3-panel-header">
        <div>
          <h3>Organ Intelligence Graph</h3>
          <p>
            {gender === "female" ? "Female" : "Male"} anatomy tree ·
            patient-aware impact reasoning
          </p>
        </div>

        <button
          type="button"
          onClick={() =>
            askMedgenCombination(
              "Analyse the selected drug combination against this patient history and current simulation. Reformulate impacted organs."
            )
          }
          disabled={streaming}
        >
          {streaming ? "Thinking..." : "Ask Medgen"}
        </button>
      </div>

      <div className="tree-toolbar">
        <button type="button" onClick={() => zoomBy(1.2)}>＋ Zoom</button>
        <button type="button" onClick={() => zoomBy(0.82)}>－ Zoom</button>
        <button type="button" onClick={() => resetZoom()}>Reset</button>
        <button type="button" onClick={() => setGraphExpanded(true)}>
          Expand Graph
        </button>

        <div className="tree-layout-toggle">
          <button
            type="button"
            className={layoutMode === "horizontal" ? "active" : ""}
            onClick={() => setLayoutMode("horizontal")}
          >
            Horizontal
          </button>
          <button
            type="button"
            className={layoutMode === "vertical" ? "active" : ""}
            onClick={() => setLayoutMode("vertical")}
          >
            Vertical
          </button>
        </div>
      </div>

      <div className="d3-tree-scroll">
        <svg ref={svgRef} />
      </div>

      {graphExpanded && (
        <div className="d3-expanded-modal">
          <div className="d3-expanded-card">
            <div className="d3-expanded-topbar">
              <div>
                <strong>Expanded Organ Intelligence Graph</strong>
                <span>
                  {gender === "female" ? "Female" : "Male"} anatomy tree · zoomable view
                </span>
              </div>

              <button type="button" onClick={() => setGraphExpanded(false)}>
                ✕
              </button>
            </div>

            <div className="tree-toolbar expanded">
              <button type="button" onClick={() => zoomBy(1.2, true)}>＋ Zoom</button>
              <button type="button" onClick={() => zoomBy(0.82, true)}>－ Zoom</button>
              <button type="button" onClick={() => resetZoom(true)}>Reset</button>

              <div className="tree-layout-toggle">
                <button
                  type="button"
                  className={layoutMode === "horizontal" ? "active" : ""}
                  onClick={() => setLayoutMode("horizontal")}
                >
                  Horizontal
                </button>
                <button
                  type="button"
                  className={layoutMode === "vertical" ? "active" : ""}
                  onClick={() => setLayoutMode("vertical")}
                >
                  Vertical
                </button>
              </div>
            </div>

            <div className="d3-tree-scroll expanded">
              <svg ref={expandedSvgRef} />
            </div>
          </div>
        </div>
      )}

      <div className="selected-organ-detail">
        <h4>
          {selectedOrgan ? formatOrganName(selectedOrgan) : "Select organ from graph"}
        </h4>

        {selectedOrganData ? (
          <>
            <span className={`organ-risk-pill ${selectedOrganData.risk}`}>
              {selectedOrganData.risk} · score {selectedOrganData.score}
            </span>

            {(selectedOrganData.reasons || []).map((reason, index) => (
              <p key={index}>{reason}</p>
            ))}
          </>
        ) : (
          <p>No active impact currently mapped for this organ.</p>
        )}
      </div>

      <div className="d3-chat-box">
        <h4>Medgen Research Chat</h4>

        <div className="d3-chat-messages">
          {chatMessages.map((msg, index) => (
            <div key={index} className={`d3-chat-msg ${msg.role}`}>
              {msg.content ||
                (streaming && index === chatMessages.length - 1
                  ? "Medgen is working..."
                  : "")}
            </div>
          ))}
        </div>

        <div className="d3-chat-input">
          {pendingImpacts.length > 0 && (
            <button
              type="button"
              className="apply-impact-btn"
              onClick={() => {
                onImpactsFromMedgen?.(pendingImpacts);
                setPendingImpacts([]);
                setChatMessages((prev) => [
                  ...prev,
                  {
                    role: "assistant",
                    content: "Applied impacts to body simulation.",
                  },
                ]);
              }}
            >
              Apply {pendingImpacts.length} impacts to simulation
            </button>
          )}
          <input
            value={chatInput}
            placeholder="Ask about drug combination, BMI, liver, kidney..."
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && askMedgenCombination()}
          />
          <button
            type="button"
            onClick={() => askMedgenCombination()}
            disabled={streaming}
          >
            Send
          </button>
        </div>
      </div>
    </aside>
  );
}

function getNodePosition(d, layoutMode) {
  if (layoutMode === "horizontal") return [d.y, d.x];
  return [d.x, d.y];
}

function makeElbowPath(link, layoutMode) {
  const [sx, sy] = getNodePosition(link.source, layoutMode);
  const [tx, ty] = getNodePosition(link.target, layoutMode);

  if (layoutMode === "horizontal") {
    const midX = (sx + tx) / 2;
    return `M${sx},${sy} H${midX} V${ty} H${tx}`;
  }

  const midY = (sy + ty) / 2;
  return `M${sx},${sy} V${midY} H${tx} V${ty}`;
}

function getNodeDims(d, label) {
  const textLength = String(label || "").length;

  if (d.data.type === "root") {
    return { width: Math.max(150, textLength * 8 + 34), height: 38 };
  }

  if (d.data.type === "system") {
    return { width: Math.max(170, textLength * 7.5 + 34), height: 34 };
  }

  if (d.data.type === "subpart") {
    return { width: Math.max(118, textLength * 7 + 28), height: 28 };
  }

  return { width: Math.max(120, textLength * 7.5 + 32), height: 32 };
}

function getNodeFill(d) {
  if (d.data.type === "root") return "rgba(34, 211, 238, 0.22)";
  if (d.data.type === "system") return "rgba(129, 140, 248, 0.24)";

  if (d.data.type === "subpart") {
    if (d.data.risk !== "normal") {
      return hexToRgba(RISK_COLORS[d.data.risk], 0.28);
    }
    return "rgba(71, 85, 105, 0.28)";
  }

  return hexToRgba(RISK_COLORS[d.data.risk] || RISK_COLORS.normal, 0.28);
}

function getNodeStroke(d) {
  if (d.data.type === "root") return "#22d3ee";
  if (d.data.type === "system") return "#818cf8";

  return RISK_COLORS[d.data.risk] || RISK_COLORS.normal;
}

function hexToRgba(hex, alpha) {
  const clean = String(hex || "#64748b").replace("#", "");
  const bigint = parseInt(clean, 16);

  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

async function readMedgenStream(response, handlers = {}) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const events = buffer.split("\n\n");
    buffer = events.pop() || "";

    for (const eventText of events) {
      const lines = eventText.split("\n");

      const eventLine = lines.find((line) => line.startsWith("event:"));
      const dataLine = lines.find((line) => line.startsWith("data:"));

      const eventName = eventLine?.replace("event:", "").trim();
      const dataRaw = dataLine?.replace("data:", "").trim();

      if (!eventName || dataRaw === undefined) continue;

      let data = {};
      try {
        data = dataRaw ? JSON.parse(dataRaw) : {};
      } catch (err) {
        console.warn("Invalid JSON from Medgen stream:", dataRaw);
        continue;
      }

      if (eventName === "start") {
        handlers.onStart?.(data);
      }

      if (eventName === "progress") {
        handlers.onProgress?.(data.message || "");
      }

      if (eventName === "final") {
        handlers.onFinal?.(data);
        return;
      }

      if (eventName === "done") {
        handlers.onDone?.(data);
        return;
      }

      if (eventName === "error") {
        throw new Error(data.message || "Medgen error");
      }
    }
  }
}
